"use client";

import {Info} from "lucide-react";
import {Button} from "@/components/ui/button";
import {LocalizationKeys} from "@/lib/all-localization-keys";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {LoaderIcon} from "@/components/icons";
import {DisclaimerContent} from "@/lib/types";
import {useTranslation} from "react-i18next";
import {TEST_IDS} from "@/lib/test-ids";

interface DisclaimerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: DisclaimerContent | null;
  isAcknowledged: boolean;
  isAcknowledging: boolean;
  onAcknowledge: () => void;
}

export function DisclaimerDialog({
  open,
  onOpenChange,
  content,
  isAcknowledged,
  isAcknowledging,
  onAcknowledge,
}: DisclaimerDialogProps) {
  const {t} = useTranslation();

  if (!content) {
    return null;
  }

  const handleAcknowledge = () => {
    if (!isAcknowledged) {
      onAcknowledge();
    }
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    // Only allow closing if already acknowledged
    if (newOpen === false && !isAcknowledged) {
      return;
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]"
        hideCloseButton={!isAcknowledged}
        onEscapeKeyDown={(e) => {
          if (!isAcknowledged) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if (!isAcknowledged) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="space-y-4">
          <DialogTitle className="flex items-start gap-2 font-semibold text-xl">
            <Info className="mt-1 h-5 w-5 shrink-0" />
            {content.title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t(LocalizationKeys.DIALOG_DISCLAIMER_DESCRIPTION)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {content.paragraphs.map((paragraph, index) => (
            <p
              className="text-foreground/80 text-sm leading-relaxed"
              key={index}
            >
              {paragraph}
            </p>
          ))}
        </div>

        <DialogFooter>
          <Button
            data-testid={TEST_IDS.CLOSE_MODAL_BUTTON}
            disabled={isAcknowledging}
            onClick={handleAcknowledge}
            type="button"
          >
            {isAcknowledging ? (
              <>
                <LoaderIcon className="mr-2 h-4 w-4 animate-spin" />
                {t(LocalizationKeys.BUTTON_PROCESSING)}
              </>
            ) : isAcknowledged ? (
              t(LocalizationKeys.BUTTON_CLOSE)
            ) : (
              t(LocalizationKeys.BUTTON_I_UNDERSTAND)
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

